import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  type SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EuroIcon from '@mui/icons-material/Euro';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';

type Tier = 'top' | 'good' | 'maybe' | 'skip';

interface PendingChild {
  id: number;
  scheduledAt: string;
}

interface Candidate {
  seriesRootId: number;
  rootName: string;
  rootStatus: string | null;
  signalCampaignId: number;
  signalCampaignName: string;
  signalSendNumber: number;
  signalSentAt: string;
  daysSinceSignal: number;
  seriesSize: number;
  productId: number;
  productCode: string;
  productName: string;
  concept: string | null;
  anfoCpa: number;
  clientCodes: string[];
  recipients: number;
  opens: number;
  openRate: number;
  clicks: number;
  ctr: number;
  unsubs: number;
  unsubRate: number;
  anfos: number;
  anfoRate: number;
  cpaEtkp: number;
  realEtkp: number;
  cpaRevenue: number;
  realRevenue: number;
  tier: Tier;
  pendingChild: PendingChild | null;
  suggestedSendAt: string;
}

interface PortfolioStats {
  n: number;
  min: number | null;
  max: number | null;
  avg: number | null;
  p25: number | null;
  p50: number | null;
  p75: number | null;
  p90: number | null;
}

interface RecommendationsResponse {
  candidates: Candidate[];
  portfolioStats: PortfolioStats;
  thresholdMode: 'percentile' | 'sparse';
  tierThresholds: { top: number | null; good: number | null; maybe: number | null };
  config: {
    minRecipients: number;
    cooldownDays: number;
    lookbackDays: number;
    signalLagDays: number;
    normalize: boolean;
  };
}

const TIER_ORDER: Tier[] = ['top', 'good', 'maybe', 'skip'];
const TIER_LABELS: Record<Tier, string> = {
  top: 'Top-Pick',
  good: 'Lohnenswert',
  maybe: 'Möglich',
  skip: 'Niedrig',
};
const TIER_COLORS: Record<Tier, { bg: string; color: string }> = {
  top: { bg: '#2e7d32', color: '#fff' },
  good: { bg: '#1976d2', color: '#fff' },
  maybe: { bg: '#757575', color: '#fff' },
  skip: { bg: '#c62828', color: '#fff' },
};

const formatPercent = (value: number, decimals = 2) =>
  `${formatNumber(value * 100, decimals)} %`;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatRelativeDays = (days: number) => {
  if (days <= 0) return 'heute';
  if (days === 1) return 'vor 1 Tag';
  if (days < 30) return `vor ${days} Tagen`;
  if (days < 60) return `vor ${Math.round(days / 7)} Wochen`;
  return `vor ${Math.round(days / 30)} Monaten`;
};

const formatGermanDateTime = (iso: string) => {
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  return d.toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface ResendRecommendationsTabProps {
  onResendClick: (campaignId: number, suggestedSendAt: string) => void;
  onOpenCampaign: (campaignId: number) => void;
}

const ResendRecommendationsTab: React.FC<ResendRecommendationsTabProps> = ({
  onResendClick,
  onOpenCampaign,
}) => {
  const [data, setData] = useState<RecommendationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [normalize, setNormalize] = useState(false);
  const [showSkipTier, setShowSkipTier] = useState(false);
  const [tierFilter, setTierFilter] = useState<Tier[]>(['top', 'good', 'maybe']);
  const [productFilter, setProductFilter] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (normalize) params.set('normalize', '1');
        const response = await fetchData<RecommendationsResponse>(
          `/api/plx-mailings/resend-recommendations?${params.toString()}`,
        );
        if (cancelled) return;
        if (response.success && response.data) {
          setData(response.data);
        } else {
          setError(response.error || 'Empfehlungen konnten nicht geladen werden.');
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [normalize]);

  const allProducts = useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    data.candidates.forEach((c) => set.add(c.productCode));
    return Array.from(set).sort();
  }, [data]);

  const effectiveTierFilter = useMemo<Tier[]>(() => {
    return showSkipTier ? Array.from(new Set<Tier>([...tierFilter, 'skip'])) : tierFilter;
  }, [tierFilter, showSkipTier]);

  const filteredCandidates = useMemo(() => {
    if (!data) return [];
    return data.candidates.filter((c) => {
      if (!effectiveTierFilter.includes(c.tier)) return false;
      if (productFilter.length > 0 && !productFilter.includes(c.productCode)) return false;
      return true;
    });
  }, [data, effectiveTierFilter, productFilter]);

  const kpiCounts = useMemo(() => {
    const counts: Record<Tier, number> = { top: 0, good: 0, maybe: 0, skip: 0 };
    data?.candidates.forEach((c) => {
      counts[c.tier] += 1;
    });
    return counts;
  }, [data]);

  const avgDaysSince = useMemo(() => {
    if (!data || data.candidates.length === 0) return 0;
    const sum = data.candidates.reduce((acc, c) => acc + c.daysSinceSignal, 0);
    return Math.round(sum / data.candidates.length);
  }, [data]);

  const handleTierFilterChange = (event: SelectChangeEvent<Tier[]>) => {
    const value = event.target.value;
    const arr = typeof value === 'string' ? (value.split(',') as Tier[]) : (value as Tier[]);
    setTierFilter(arr.filter((t) => t !== 'skip'));
  };

  const handleProductFilterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setProductFilter(typeof value === 'string' ? value.split(',') : value);
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) return null;

  const { candidates, portfolioStats, thresholdMode, tierThresholds, config } = data;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ReplayIcon color="primary" />
                <Typography variant="body2" color="textSecondary">
                  Kandidaten
                </Typography>
              </Box>
              <Typography variant="h4">{formatNumber(candidates.length, 0)}</Typography>
              <Typography variant="caption" color="textSecondary">
                Cooldown ≥ {config.cooldownDays}d, Empfänger ≥ {formatNumber(config.minRecipients, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <StarIcon sx={{ color: TIER_COLORS.top.bg }} />
                <Typography variant="body2" color="textSecondary">
                  Top-Picks
                </Typography>
              </Box>
              <Typography variant="h4">{formatNumber(kpiCounts.top, 0)}</Typography>
              <Typography variant="caption" color="textSecondary">
                {`CPA-ETKP ≥ ${formatCurrency(tierThresholds.top || 0)} (P75${thresholdMode === 'sparse' ? ', wenig Daten' : ''})`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EuroIcon color="success" />
                <Typography variant="body2" color="textSecondary">
                  Ø CPA-ETKP
                </Typography>
              </Box>
              <Typography variant="h4">{formatCurrency(portfolioStats.avg || 0)}</Typography>
              <Typography variant="caption" color="textSecondary">
                Pool: €{formatNumber(portfolioStats.min || 0, 2)} – €
                {formatNumber(portfolioStats.max || 0, 2)} / N = {portfolioStats.n}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="info" />
                <Typography variant="body2" color="textSecondary">
                  Ø Tage seit Signal
                </Typography>
              </Box>
              <Typography variant="h4">{avgDaysSince}</Typography>
              <Typography variant="caption" color="textSecondary">
                Signal-Lag ≥ {config.signalLagDays}d (ANFOs eingelaufen)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {thresholdMode === 'sparse' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Wenig Signal im Pool (N&nbsp;&lt;&nbsp;10) — die Tier-Schwellen (P75 / P50 / P25: Top
          ≥&nbsp;{formatCurrency(tierThresholds.top || 0)}, Lohnenswert ≥&nbsp;
          {formatCurrency(tierThresholds.good || 0)}, Möglich ≥&nbsp;
          {formatCurrency(tierThresholds.maybe || 0)}) beruhen auf wenigen Kampagnen und sind
          entsprechend unsicher.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <Box sx={{ minWidth: 220 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
            Tier
          </Typography>
          <Select
            multiple
            size="small"
            fullWidth
            value={tierFilter}
            onChange={handleTierFilterChange}
            input={<OutlinedInput />}
            renderValue={(selected) =>
              (selected as Tier[]).map((t) => TIER_LABELS[t]).join(', ') || 'Alle aktiven'
            }
          >
            {TIER_ORDER.filter((t) => t !== 'skip').map((tier) => (
              <MenuItem key={tier} value={tier}>
                <Checkbox checked={tierFilter.includes(tier)} />
                <ListItemText primary={TIER_LABELS[tier]} />
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ minWidth: 240 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
            Produkt
          </Typography>
          <Select
            multiple
            size="small"
            fullWidth
            value={productFilter}
            onChange={handleProductFilterChange}
            input={<OutlinedInput />}
            displayEmpty
            renderValue={(selected) =>
              (selected as string[]).length === 0
                ? 'Alle Produkte'
                : (selected as string[]).join(', ')
            }
          >
            {allProducts.map((p) => (
              <MenuItem key={p} value={p}>
                <Checkbox checked={productFilter.includes(p)} />
                <ListItemText primary={p} />
              </MenuItem>
            ))}
          </Select>
        </Box>

        <FormControlLabel
          control={
            <Switch checked={showSkipTier} onChange={(e) => setShowSkipTier(e.target.checked)} />
          }
          label="Niedrig-Tier einblenden"
        />
        <FormControlLabel
          control={
            <Switch checked={normalize} onChange={(e) => setNormalize(e.target.checked)} />
          }
          label="Inaktive-Sends normalisieren"
        />
      </Box>

      {filteredCandidates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#ffffff' }}>
          <EventBusyIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Keine Kandidaten im aktuellen Filter
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Pool wächst, sobald weitere Sends das {config.cooldownDays}-Tage-Cooldown verlassen.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ backgroundColor: '#ffffff', maxHeight: 720 }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {[
                  'Tier',
                  'Kampagne',
                  'Produkt',
                  'Letzter Send',
                  'Send #',
                  'Empfänger',
                  'CPA-ETKP €',
                  'ANFOs',
                  'ANFO-%',
                  'CTR %',
                  'Unsub %',
                  'Audience',
                  'Aktion',
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCandidates.map((c) => {
                const tierColor = TIER_COLORS[c.tier];
                const hasPending = c.pendingChild !== null;
                const isSeriesRoot = c.seriesRootId === c.signalCampaignId;
                const displayName = isSeriesRoot ? c.rootName : `${c.rootName} (#${c.signalSendNumber})`;

                return (
                  <TableRow
                    key={c.seriesRootId}
                    hover
                    sx={{ opacity: hasPending ? 0.55 : 1 }}
                  >
                    <TableCell>
                      <Chip
                        label={TIER_LABELS[c.tier]}
                        size="small"
                        sx={{
                          backgroundColor: tierColor.bg,
                          color: tierColor.color,
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Tooltip title={c.signalCampaignName}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {displayName}
                        </Typography>
                      </Tooltip>
                      {c.seriesSize > 1 && (
                        <Typography variant="caption" color="textSecondary">
                          Serie: {c.seriesSize} Sends
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={c.productName}>
                        <Chip label={c.productCode} size="small" variant="outlined" />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatGermanDateTime(c.signalSentAt)}>
                        <Typography variant="body2">
                          {formatRelativeDays(c.daysSinceSignal)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>#{c.signalSendNumber}</TableCell>
                    <TableCell align="right">{formatNumber(c.recipients, 0)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(c.cpaEtkp)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(c.anfos, 0)}</TableCell>
                    <TableCell align="right">{formatPercent(c.anfoRate, 3)}</TableCell>
                    <TableCell align="right">{formatPercent(c.ctr, 2)}</TableCell>
                    <TableCell align="right">{formatPercent(c.unsubRate, 3)}</TableCell>
                    <TableCell>
                      {c.clientCodes.map((cc) => (
                        <Chip
                          key={cc}
                          label={cc}
                          size="small"
                          sx={{ mr: 0.3, height: 18, fontSize: '0.65rem' }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      {hasPending && c.pendingChild ? (
                        <Tooltip
                          title={`Geplanter Send: ${formatGermanDateTime(c.pendingChild.scheduledAt)}`}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<EditIcon />}
                            onClick={() => onOpenCampaign(c.pendingChild!.id)}
                          >
                            Bereits geplant
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<ReplayIcon />}
                          onClick={() => onResendClick(c.signalCampaignId, c.suggestedSendAt)}
                        >
                          Resend anlegen
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="caption" color="textSecondary">
          Portfolio: P25 = {formatCurrency(portfolioStats.p25 || 0)} | P50 ={' '}
          {formatCurrency(portfolioStats.p50 || 0)} | P75 ={' '}
          {formatCurrency(portfolioStats.p75 || 0)} | P90 ={' '}
          {formatCurrency(portfolioStats.p90 || 0)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Lookback: {config.lookbackDays}d · Cooldown: {config.cooldownDays}d · Signal-Lag:{' '}
          {config.signalLagDays}d
        </Typography>
      </Box>
    </Box>
  );
};

export default ResendRecommendationsTab;
